// Pont entre le VOLET et le GESTIONNAIRE D'ÉVÉNEMENT de rédaction.
//
// POURQUOI CE FICHIER EXISTE. Le gestionnaire d'événement `messageComposeOpened`
// tourne, sur Outlook classique Windows, dans un runtime « JavaScript seul » :
// pas de DOM, pas de `localStorage`, pas de cookies, et surtout
// `Office.context.auth` ET `Office.context.ui.displayDialogAsync` sont
// EXPLICITEMENT bloqués pour les add-ins activés par événement. Il ne peut donc
// ni s'authentifier, ni appeler notre API, ni afficher quoi que ce soit d'autre
// que la bannière.
//
// Il lui faut pourtant savoir si la préférence « Envoyer et ranger » est active,
// sinon la bannière s'imposerait à tout le monde. Le volet, qui LUI est
// authentifié, recopie donc la préférence dans une mémoire partagée ; le
// gestionnaire ne fait que la lire.
//
// DEUX MÉMOIRES, ET NON UNE — c'est la correction du 2026-07-30, après que la
// bannière ne soit jamais sortie sur un client où tout le reste fonctionnait.
// `OfficeRuntime.storage` seul ne suffit pas, parce que le runtime du
// gestionnaire N'EST PAS LE MÊME selon l'hôte (doc « Runtimes in Office
// Add-ins ») :
//
//   • Outlook classique Windows  → runtime « JavaScript seul » : pas de
//     `localStorage`, mais `OfficeRuntime.storage` y est disponible.
//   • Outlook sur le web, nouvel Outlook Windows, Mac → runtime NAVIGATEUR :
//     `localStorage` y est disponible et PARTAGÉ avec le volet (même origine,
//     même site de premier niveau, donc même partition de stockage).
//
// La consigne Microsoft pour partager entre un événement Outlook et un volet est
// d'ailleurs de « brancher son code sur `Office.context.platform` ». On écrit
// dans les DEUX mémoires plutôt que de brancher : le volet ne peut pas savoir
// dans quel type de runtime tournera l'événement à venir, et une écriture ratée
// dans l'une n'empêche pas l'autre. La lecture accepte l'une OU l'autre.
//
// `Office.sessionData`, que la doc cite pour Windows, ne convient pas ici : il
// est propre à UN élément en cours de rédaction, alors que la préférence doit
// être connue AVANT qu'un brouillon n'existe.
//
// CONSÉQUENCE ASSUMÉE : sur un poste où le volet n'a jamais été ouvert, le drapeau
// est absent et la bannière ne s'affiche pas. C'est le bon sens de l'erreur, et le
// même que celui de la migration 0025 : l'inconnu se résout en « désactivé ».
//
// CE FICHIER NE DOIT RIEN IMPORTER. Il est embarqué dans le bundle du
// gestionnaire d'événement, qui doit rester un fichier autonome et léger (« you
// must bundle your event-handling JavaScript code into this single file [...] a
// large JavaScript bundle may cause issues with the performance of your add-in »).

/** Clé du drapeau dans `OfficeRuntime.storage`. Préfixée : le stockage est plat. */
export const SEND_AND_FILE_FLAG = 'prism.sendAndFile';

/** Identifiant de NOTRE bannière. Une seule `InsightMessage` par add-in et par mail. */
export const SEND_AND_FILE_NOTIFICATION = 'prismSendAndFile';

/**
 * `id` du bouton de ruban en contexte `mailCompose`, cité par `commandId` dans
 * l'action de la bannière — « the button defined in the manifest ». Il doit rester
 * identique ici et dans `manifest.json`, sans quoi le lien de la bannière ne
 * résout rien : Office n'a alors aucun volet à ouvrir.
 */
export const COMPOSE_COMMAND_ID = 'composeOpenBtn';

/** Marqueur passé en `contextData`, relu par le volet à son ouverture. */
export const SEND_AND_FILE_CONTEXT = 'sendAndFile';

/**
 * Les deux seules méthodes que l'on utilise de `OfficeRuntime.storage`.
 *
 * DÉCLARÉES ICI PLUTÔT QU'IMPORTÉES : `@types/office-js` ne décrit PAS le
 * namespace `OfficeRuntime` (il ne le mentionne que dans un commentaire) ; ses
 * types vivent dans un paquet distinct. Ajouter une dépendance entière pour deux
 * signatures serait disproportionné, d'autant que ce fichier est embarqué dans le
 * bundle du gestionnaire d'événement, qui doit rester minuscule.
 */
interface RuntimeStorage {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
}

declare const OfficeRuntime: { storage?: RuntimeStorage } | undefined;

function storage(): RuntimeStorage | null {
  // Optionnel à dessein : `OfficeRuntime` n'existe pas sur les hôtes anciens, et
  // une exception ici casserait l'ouverture d'une fenêtre de rédaction — un prix
  // absurde pour une bannière. Le `typeof` est OBLIGATOIRE et non superflu :
  // lire un identifiant global absent lève une ReferenceError, que ni `?.` ni un
  // test de nullité n'interceptent.
  try {
    return typeof OfficeRuntime !== 'undefined' ? OfficeRuntime?.storage ?? null : null;
  } catch {
    return null;
  }
}

/**
 * `localStorage`, ou `null` dans un runtime « JavaScript seul » — où il n'existe
 * pas — et dans un navigateur qui le refuse (cookies tiers bloqués, mode privé
 * strict). Même précaution de `typeof` que ci-dessus, pour la même raison.
 */
function webStorage(): Pick<Storage, 'getItem' | 'setItem'> | null {
  try {
    return typeof localStorage !== 'undefined' ? localStorage : null;
  } catch {
    return null;
  }
}

/**
 * Le drapeau vaut-il « actif » ?
 *
 * VRAI SI L'UNE DES DEUX MÉMOIRES LE DIT, et non les deux : sur un hôte donné,
 * une seule des deux est effectivement partagée avec le runtime du gestionnaire
 * (cf. l'en-tête). Exiger l'accord des deux revenait à n'afficher la bannière
 * nulle part.
 *
 * Tout échec — mémoire absente, clé jamais écrite, valeur illisible — rend
 * `false`. Ne pas afficher la bannière est toujours préférable à l'afficher à
 * tort.
 */
export async function readSendAndFileFlag(): Promise<boolean> {
  const web = webStorage();
  try {
    if (web?.getItem(SEND_AND_FILE_FLAG) === 'true') return true;
  } catch {
    // Un `localStorage` présent mais inaccessible ne doit pas empêcher la
    // lecture de l'autre mémoire.
  }

  const s = storage();
  if (!s) return false;
  try {
    return (await s.getItem(SEND_AND_FILE_FLAG)) === 'true';
  } catch {
    return false;
  }
}

/**
 * Recopie la préférence pour le gestionnaire d'événement, dans les DEUX mémoires.
 *
 * Écrit `'false'` au lieu d'effacer la clé : « absente » et « désactivée » se
 * comportent pareil à la lecture, mais une écriture explicite rend l'état
 * observable quand on diagnostique un poste où la bannière ne sort pas — et
 * surtout, elle ÉTEINT la bannière quand l'utilisateur décoche la préférence, ce
 * qu'une suppression de clé ferait aussi mais sans trace.
 *
 * Les deux écritures sont INDÉPENDANTES : l'échec de l'une ne doit pas priver
 * l'autre, puisque c'est justement l'autre qui porte l'hôte courant.
 */
export async function writeSendAndFileFlag(enabled: boolean): Promise<void> {
  const value = enabled ? 'true' : 'false';

  try {
    webStorage()?.setItem(SEND_AND_FILE_FLAG, value);
  } catch {
    // Quota, mode privé, stockage cloisonné : rien à faire, et rien à dire.
  }

  const s = storage();
  if (!s) return;
  try {
    await s.setItem(SEND_AND_FILE_FLAG, value);
  } catch {
    // Silencieux : la préférence reste vraie côté serveur, seule la bannière en
    // souffre, et le volet a mieux à faire que d'alerter là-dessus.
  }
}
